import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import BulkAssignTniModal from "@/components/BulkAssignTniModal";
import { MONTHS, ROLE_LABELS } from "@/lib/constants";
import {
  AlertTriangle, Calendar as CalendarIcon, ClipboardCheck,
  Flag, GraduationCap, Target, ArrowRight, Check, UserPlus, Users, ClipboardPlus,
} from "lucide-react";

function Metric({ label, subtitle, value, accent, testid, icon: Icon, footnote, to, loading }) {
  const inner = (
    <div className="bc-card p-5 h-full group transition-all border border-[#E5E5E5] group-hover:border-[#C41E1E]" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="bc-label group-hover:text-[#C41E1E] transition-colors">{label}</div>
          {loading ? (
            <div className="h-9 w-16 mt-2 bg-[#E5E5E5] rounded animate-pulse" data-testid={`${testid}-skeleton`} />
          ) : (
            <div className={`font-heading text-3xl font-bold mt-2 ${accent || "text-[#0D0D0D]"} group-hover:underline underline-offset-4 decoration-[#C41E1E]`}>{value}</div>
          )}
          {subtitle && !loading && (
            <p className="text-[11px] text-[#999999] mt-1.5 leading-tight">{subtitle}</p>
          )}
          {footnote && !loading && (
            <div className="text-[10px] uppercase tracking-wider text-[#999999] mt-2" data-testid={`${testid}-fy-label`}>
              {footnote}
            </div>
          )}
        </div>
        {Icon && <Icon size={18} className="text-[#666666] group-hover:text-[#C41E1E] transition-colors shrink-0" />}
      </div>
    </div>
  );
  return to ? (
    <Link to={to} className="block" data-testid={`${testid}-link`}>
      {inner}
    </Link>
  ) : inner;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [pendingTests, setPendingTests] = useState([]);
  const [calendarItems, setCalendarItems] = useState([]);
  const [majorFlags, setMajorFlags] = useState([]);
  const [employees, setEmployees] = useState({});
  const [loadedAt, setLoadedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unassessed, setUnassessed] = useState(null); // { total, employees: [...] }
  const [assignTarget, setAssignTarget] = useState(null);

  const isEmployee = user.system_role === "employee";
  const isReadOnly = user.system_role === "quality" || user.system_role === "management";
  const canAssignBulk = ["hr", "super_admin"].includes(user.system_role);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/summary").then((r) => r.data),
      api.get("/assessments", { params: { status: "pending" } }).then((r) => r.data),
      api.get("/calendar", { params: { year: new Date().getFullYear() } }).then((r) => r.data),
      api.get("/gaps", { params: { flag_major: true } }).then((r) => r.data),
      api.get("/employees").then((r) => r.data),
    ]).then(([s, tests, cal, majors, emps]) => {
      setSummary(s);
      setPendingTests(isEmployee ? tests.filter((t) => t.employee_id === user.employee_id) : tests);
      setCalendarItems(cal);
      setMajorFlags(majors);
      setEmployees(Object.fromEntries(emps.map((e) => [e.employee_id, e])));
      setLoadedAt(new Date());
      setLoading(false);
    });
  }, [user.employee_id, user.system_role, isEmployee]);

  // Unassessed-employees panel — HR & Super Admin only.
  const [unassessedTick, setUnassessedTick] = useState(0);
  useEffect(() => {
    if (!canAssignBulk) return;
    api.get("/dashboard/unassessed-employees")
      .then((r) => setUnassessed(r.data))
      .catch(() => setUnassessed({ total: 0, employees: [] }));
  }, [canAssignBulk, unassessedTick]);

  const today = new Date();
  const todayISO = today.toISOString().slice(0, 10);
  const thisMonth = today.getMonth() + 1;
  const thisMonthTrainings = calendarItems.filter((c) => c.target_month === thisMonth);

  const overdueTests = pendingTests.filter((t) => t.due_date && t.due_date < todayISO);
  const upcomingTests = pendingTests.filter((t) => !t.due_date || t.due_date >= todayISO);

  // "Today's Actions" — top-5 prioritised list.
  const emptyNomineeTrainings = calendarItems.filter(
    (c) => c.status === "planned" && (c.nominated_participants || []).length === 0
  );
  const actions = [];
  overdueTests.slice(0, 3).forEach((t) => {
    actions.push({
      key: `overdue-${t.assessment_id}`,
      icon: AlertTriangle,
      iconClass: "text-[#C41E1E]",
      title: `${employees[t.employee_id]?.name || `#${t.employee_id}`} — ${t.skill_name}`,
      subtitle: `Overdue · ${t.type} · Due ${t.due_date}`,
      cta: "View",
      to: `/my-tests?status=overdue`,
    });
  });
  emptyNomineeTrainings.slice(0, 3).forEach((c) => {
    actions.push({
      key: `nom-${c.calendar_id}`,
      icon: UserPlus,
      iconClass: "text-[#C9A84C]",
      title: `${c.skill_name} — ${MONTHS[(c.target_month || 1) - 1]}`,
      subtitle: "Planned training has no nominees yet",
      cta: "Nominate",
      to: `/calendar/${c.calendar_id}`,
    });
  });
  majorFlags.slice(0, 3).forEach((n) => {
    actions.push({
      key: `flag-${n.employee_id}-${n.skill_code}`,
      icon: Flag,
      iconClass: "text-[#C41E1E]",
      title: `${n.employee_name || employees[n.employee_id]?.name || `#${n.employee_id}`} — ${n.skill_name}`,
      subtitle: `Effectiveness failed (${n.achieved_level} < ${n.required_level}) · re-training required`,
      cta: "View",
      to: `/card/${n.employee_id}`,
    });
  });
  const topActions = actions.slice(0, 5);

  const timeLabel = loadedAt ? loadedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "…";

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow={`${ROLE_LABELS[user.system_role]} workspace`}
        title={`Welcome, ${user.name.split(" ")[0]}`}
        subtitle={
          <span data-testid="dashboard-updated-at">
            Dashboard updated as of {timeLabel}
            {summary?.fy_label ? ` · ${summary.fy_label}` : ""}
          </span>
        }
        breadcrumbs={[{ label: "Home" }]}
      />

      <div className="px-4 md:px-10 py-8 space-y-8">
        {/* Today's Actions — only for non-employee users */}
        {!isEmployee && (
          <section data-testid="todays-actions">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="bc-label">Today&apos;s actions</div>
                <h3 className="font-heading text-lg font-semibold mt-0.5">
                  {isReadOnly ? "Live workforce signals" : "What needs attention today"}
                </h3>
              </div>
            </div>
            {loading ? (
              <div
                className="bc-card px-5 py-4 flex items-center gap-3"
                data-testid="todays-actions-loading"
              >
                <div className="h-4 w-4 rounded-full border-2 border-[#C41E1E] border-t-transparent animate-spin shrink-0" />
                <span className="text-sm text-[#666666]">Loading actions…</span>
              </div>
            ) : topActions.length === 0 ? (
              <div
                className="bc-card px-5 py-4 flex items-center gap-3 bg-green-50 border border-green-200"
                data-testid="todays-actions-empty"
              >
                <Check size={16} className="text-green-700" />
                <span className="text-sm font-medium text-green-800">All clear — no urgent actions today</span>
              </div>
            ) : (
              <div className="bc-card divide-y divide-[#E5E5E5]" data-testid="todays-actions-list">
                {topActions.map((a) => {
                  const Icon = a.icon;
                  const RowInner = (
                    <>
                      <div className="flex items-center gap-3">
                        <Icon size={16} className={a.iconClass} />
                        <div>
                          <div className="font-medium text-sm">{a.title}</div>
                          <div className="text-xs text-[#666666]">{a.subtitle}</div>
                        </div>
                      </div>
                      {!isReadOnly && (
                        <span className="text-xs text-[#C41E1E] font-medium inline-flex items-center gap-1">
                          {a.cta} <ArrowRight size={12} />
                        </span>
                      )}
                    </>
                  );
                  return isReadOnly ? (
                    <div key={a.key} data-testid={`action-${a.key}`} className="px-5 py-3 flex items-center justify-between">
                      {RowInner}
                    </div>
                  ) : (
                    <Link
                      key={a.key}
                      to={a.to}
                      data-testid={`action-${a.key}`}
                      className="px-5 py-3 flex items-center justify-between bc-row-hover"
                    >
                      {RowInner}
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Metrics */}
        {isEmployee ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric testid="metric-my-pending" label="My pending tests" value={summary?.my_pending ?? "—"} icon={ClipboardCheck} accent="text-[#C41E1E]" footnote={summary?.fy_label} to="/my-tests?status=pending" loading={loading} />
            <Metric testid="metric-my-overdue" label="My overdue" value={summary?.my_overdue ?? "—"} icon={AlertTriangle} accent={summary?.my_overdue ? "text-[#C41E1E]" : ""} footnote={summary?.fy_label} to="/my-tests?status=overdue" loading={loading} />
            <Metric testid="metric-month-training" label="Trainings this month" value={`${summary?.trainings_conducted_this_month ?? 0}/${summary?.trainings_planned_this_month ?? 0}`} icon={GraduationCap} loading={loading} />
            <Metric testid="metric-employees" label="Active employees" value={summary?.employees_total ?? "—"} icon={Target} loading={loading} />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric
              testid="metric-pending"
              label={user.system_role === "hr" || user.system_role === "super_admin" ? "Pending assessments" : "Org pending assessments"}
              subtitle="Tests assigned but not yet completed"
              value={summary?.pending_assessments ?? "—"}
              icon={ClipboardCheck}
              accent="text-[#C9A84C]"
              footnote={summary?.fy_label}
              to="/my-tests?status=pending"
              loading={loading}
            />
            <Metric
              testid="metric-overdue"
              label="Overdue"
              subtitle="Assigned tests past their due date"
              value={summary?.overdue_assessments ?? "—"}
              icon={AlertTriangle}
              accent={summary?.overdue_assessments ? "text-[#C41E1E]" : ""}
              footnote={summary?.fy_label}
              to="/my-tests?status=overdue"
              loading={loading}
            />
            <Metric
              testid="metric-gaps"
              label="Open gaps"
              subtitle="Assessed skills below required level"
              value={summary?.open_gaps ?? "—"}
              icon={Target}
              footnote={summary?.fy_label}
              to="/tni-matrix/staff?gaps=1"
              loading={loading}
            />
            <Metric
              testid="metric-major"
              label="Major flags"
              subtitle="Failed post-training effectiveness tests"
              value={summary?.major_flags ?? "—"}
              icon={Flag}
              accent={summary?.major_flags ? "text-[#C41E1E]" : ""}
              footnote={summary?.fy_label}
              to="/my-tests?flag=major"
              loading={loading}
            />
          </div>
        )}

        {/* Sections */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Pending tests — split into Overdue / Pending */}
          <div className="space-y-4">
            <PendingBlock
              title="Overdue — Action Required"
              subtitle="These employees have TNI or effectiveness tests past their due date"
              tone="danger"
              rows={overdueTests}
              employees={employees}
              user={user}
              isReadOnly={isReadOnly}
              testid="overdue-tests-panel"
              emptyText="No overdue tests. ✓"
            />
            <PendingBlock
              title="Pending"
              subtitle="Tests assigned and due in future"
              tone="amber"
              rows={upcomingTests}
              employees={employees}
              user={user}
              isReadOnly={isReadOnly}
              testid="pending-tests-panel"
              emptyText="No pending tests."
            />
          </div>

          {/* This month trainings */}
          <div className="bc-card h-fit">
            <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
              <div>
                <div className="bc-label">{MONTHS[today.getMonth()]} {today.getFullYear()}</div>
                <h3 className="font-heading text-lg font-semibold mt-0.5">Trainings this month</h3>
                <p className="text-[11px] text-[#999999] mt-0.5 leading-tight">Planned and conducted training sessions in the current calendar month</p>
              </div>
              <Link to="/calendar" className="text-xs text-[#C41E1E] font-medium flex items-center gap-1">
                View calendar <ArrowRight size={12} />
              </Link>
            </div>
            <div className="divide-y divide-[#E5E5E5]" data-testid="this-month-trainings">
              {thisMonthTrainings.length === 0 && (
                <div className="px-5 py-8 text-sm text-[#666666] text-center">No trainings scheduled this month.</div>
              )}
              {thisMonthTrainings.map((c) => {
                const noNominees = (c.nominated_participants || []).length === 0 && c.status === "planned";
                return (
                  <div key={c.calendar_id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`month-training-${c.calendar_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">{c.skill_name}</div>
                      <div className="text-xs text-[#666666] mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{(c.nominated_participants || []).length} nominated · {c.source === "TNI_driven" ? "TNI-driven" : "Mandatory"}</span>
                        {noNominees && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-amber-50 border border-amber-300 text-amber-800 rounded-sm"
                            data-testid={`no-nominees-${c.calendar_id}`}
                          >
                            <AlertTriangle size={10} /> No nominees yet
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <StatusPill status={c.status} />
                      {noNominees && !isReadOnly && (
                        <Link
                          to={`/calendar/${c.calendar_id}`}
                          data-testid={`nominate-${c.calendar_id}`}
                          className="text-xs text-[#C41E1E] font-medium inline-flex items-center gap-1"
                        >
                          Nominate <ArrowRight size={12} />
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Major flags — collapse when empty */}
          {!isEmployee && majorFlags.length === 0 && (
            <div
              className="lg:col-span-2 text-xs text-[#999999] flex items-center gap-1.5"
              data-testid="major-flags-empty"
            >
              <Check size={12} /> No re-training flags
            </div>
          )}
          {!isEmployee && majorFlags.length > 0 && (
            <div className="bc-card lg:col-span-2" data-testid="major-flags-section">
              <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-start gap-2">
                <Flag size={16} className="text-[#C41E1E] mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-heading text-lg font-semibold">Major flags — re-training required</h3>
                  <p className="text-[11px] text-[#999999] mt-0.5 leading-tight">Employees who failed an effectiveness assessment and must be re-trained</p>
                </div>
              </div>
              <div className="divide-y divide-[#E5E5E5]">
                {majorFlags.map((n) => (
                  <div key={`${n.employee_id}-${n.skill_code}`} className="px-5 py-3 flex items-center justify-between bg-red-50/40">
                    <div>
                      <div className="font-medium text-sm">{n.employee_name || employees[n.employee_id]?.name || `Employee #${n.employee_id}`}</div>
                      <div className="text-xs text-[#666666]">{n.skill_name} · Effectiveness failed ({n.achieved_level} &lt; {n.required_level})</div>
                    </div>
                    {!isReadOnly ? (
                      <Link
                        to={`/card/${n.employee_id}`}
                        data-testid={`major-view-${n.employee_id}-${n.skill_code}`}
                        className="text-xs text-[#C41E1E] font-medium inline-flex items-center gap-1"
                      >
                        View Employee <ArrowRight size={12} />
                      </Link>
                    ) : (
                      <StatusPill status="open" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Unassessed employees — HR & Super Admin only */}
          {canAssignBulk && unassessed && unassessed.total === 0 && (
            <div
              className="lg:col-span-2 text-xs text-green-700 flex items-center gap-1.5"
              data-testid="unassessed-empty"
            >
              <Check size={12} /> All employees have current FY assessments or active assignments
            </div>
          )}
          {canAssignBulk && unassessed && unassessed.total > 0 && (
            <div className="bc-card lg:col-span-2" data-testid="unassessed-section">
              <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
                <div className="flex items-start gap-2">
                  <UserPlus size={16} className="text-amber-700 mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-heading text-lg font-semibold">
                      Employees with pending TNI assignments ({unassessed.total})
                    </h3>
                    <p className="text-[11px] text-[#999999] mt-0.5 leading-tight">Required skills with no test assigned or completed in the current financial year</p>
                  </div>
                </div>
                {unassessed.total > 5 && (
                  <Link
                    to="/employees?unassessed=1"
                    data-testid="unassessed-view-all"
                    className="text-xs text-[#C41E1E] font-medium inline-flex items-center gap-1"
                  >
                    View all {unassessed.total} <ArrowRight size={12} />
                  </Link>
                )}
              </div>
              <div className="divide-y divide-[#E5E5E5]">
                {unassessed.employees.slice(0, 5).map((e) => (
                  <div
                    key={e.employee_id}
                    className="px-5 py-3 flex items-center justify-between gap-3 bc-row-hover"
                    data-testid={`unassessed-row-${e.employee_id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-sm">{e.name}</div>
                      <div className="text-xs mt-0.5 flex items-center gap-1.5">
                        <span className="text-[#666666]">{e.department || "—"}</span>
                        <span className="text-[#CCCCCC]">·</span>
                        <span className="text-amber-700 font-medium">
                          {e.missing_skill_count} skill{e.missing_skill_count === 1 ? "" : "s"} need TNI assignment
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAssignTarget({ employee_id: e.employee_id, name: e.name, missing_skill_codes: e.missing_skill_codes })}
                      data-testid={`unassessed-assign-${e.employee_id}`}
                      className="inline-flex items-center gap-1 text-xs text-white bg-[#C41E1E] hover:bg-[#a71919] rounded-sm px-2.5 py-1.5 shrink-0"
                    >
                      <ClipboardPlus size={12} />
                      Assign Tests <ArrowRight size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <BulkAssignTniModal
        open={!!assignTarget}
        employee={assignTarget}
        filterSkillCodes={assignTarget?.missing_skill_codes}
        onClose={() => setAssignTarget(null)}
        onSuccess={() => setUnassessedTick((t) => t + 1)}
      />
    </div>
  );
}

function PendingBlock({ title, subtitle, tone, rows, employees, user, isReadOnly, testid, emptyText }) {
  const headerCls = tone === "danger"
    ? "bg-red-50 border-b border-red-200 text-[#8B0000]"
    : "bg-amber-50 border-b border-amber-200 text-amber-900";
  return (
    <div className="bc-card" data-testid={testid}>
      <div className={`px-5 py-3 flex items-center justify-between ${headerCls}`}>
        <div>
          <h3 className="font-heading text-sm font-semibold uppercase tracking-wider">{title}</h3>
          {subtitle && (
            <p className="text-[11px] mt-0.5 leading-tight opacity-70 font-normal normal-case tracking-normal">{subtitle}</p>
          )}
        </div>
        <span className="text-xs font-mono font-semibold">{rows.length}</span>
      </div>
      <div className="divide-y divide-[#E5E5E5]">
        {rows.length === 0 && (
          <div className="px-5 py-6 text-xs text-[#666666] text-center">{emptyText}</div>
        )}
        {rows.slice(0, 5).map((t) => {
          const empName = employees[t.employee_id]?.name || `#${t.employee_id}`;
          return (
            <div
              key={t.assessment_id}
              className="px-5 py-3 flex items-center justify-between gap-3 bc-row-hover"
              data-testid={`pending-row-${t.assessment_id}`}
            >
              <div className="min-w-0">
                <div className="font-medium text-sm">{t.skill_name}</div>
                <div className="text-xs text-[#666666] flex items-center gap-1.5 mt-0.5">
                  <Users size={10} />
                  <span>{user.system_role === "employee" ? `${t.type} · Due ${t.due_date}` : `${empName} · ${t.type} · Due ${t.due_date || "—"}`}</span>
                </div>
              </div>
              {!isReadOnly && (
                <Link
                  to={user.system_role === "employee" || t.employee_id === user.employee_id ? `/test/${t.assessment_id}` : `/card/${t.employee_id}`}
                  data-testid={`pending-view-${t.assessment_id}`}
                  className="text-xs text-[#C41E1E] font-medium shrink-0 inline-flex items-center gap-1"
                >
                  View Employee <ArrowRight size={12} />
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
